import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    timeout: 20000,
    withCredentials: true,
});

// Attach a bearer token if we captured one (helps Playwright / tools that ignore Set-Cookie)
export const setAuthToken = (token) => {
    if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete api.defaults.headers.common.Authorization;
};

export const endpoints = {
    discover: () => api.get("/discover").then((r) => r.data),
    meta: () => api.get("/meta").then((r) => r.data),

    listArtists: (params) => api.get("/artists", { params }).then((r) => r.data),
    getArtist: (id) => api.get(`/artists/${id}`).then((r) => r.data),
    followArtist: (id) => api.post(`/artists/${id}/follow`).then((r) => r.data),
    updateArtist: (id, payload) => api.put(`/artists/${id}`, payload).then((r) => r.data),

    listEvents: (params) => api.get("/events", { params }).then((r) => r.data),
    getEvent: (id) => api.get(`/events/${id}`).then((r) => r.data),
    registerEvent: (id, payload) => api.post(`/events/${id}/register`, payload).then((r) => r.data),
    fanCancelRegistration: (eventId) => api.post(`/fan/events/${eventId}/cancel`).then((r) => r.data),
    createEvent: (payload) => api.post("/events", payload).then((r) => r.data),
    updateEvent: (id, payload) => api.put(`/events/${id}`, payload).then((r) => r.data),
    deleteEvent: (id) => api.delete(`/events/${id}`).then((r) => r.data),
    updateEventAgreement: (eventId, agreement) => api.put(`/events/${eventId}/agreement`, agreement).then((r) => r.data),
    updateEventActualRevenue: (eventId, body) => api.put(`/events/${eventId}/actual-revenue`, body).then((r) => r.data),
    markEventSettled: (eventId) => api.post(`/events/${eventId}/settle`).then((r) => r.data),
    reopenEventSettlement: (eventId) => api.post(`/events/${eventId}/reopen-settlement`).then((r) => r.data),

    listVenues: (params) => api.get("/venues", { params }).then((r) => r.data),

    eventAttendees: (id, params) =>
        api.get(`/events/${id}/attendees`, { params }).then((r) => r.data),
    checkIn: (eventId, regId) =>
        api.post(`/events/${eventId}/checkin/${regId}`).then((r) => r.data),
    undoCheckIn: (eventId, regId) =>
        api.post(`/events/${eventId}/checkin/${regId}/undo`).then((r) => r.data),
    addWalkin: (eventId, payload) =>
        api.post(`/events/${eventId}/walkin`, payload).then((r) => r.data),

    dashboard: (artistId) =>
        api.get(`/workspace/${artistId}/dashboard`).then((r) => r.data),
    workspaceEvents: (artistId, params) =>
        api.get(`/workspace/${artistId}/events`, { params }).then((r) => r.data),
    audience: (artistId) =>
        api.get(`/workspace/${artistId}/audience`).then((r) => r.data),
    business: (artistId) =>
        api.get(`/workspace/${artistId}/business`).then((r) => r.data),
    team: (artistId) => api.get(`/workspace/${artistId}/team`).then((r) => r.data),

    // Fan Passport
    fanMe: () => api.get("/fan/me").then((r) => r.data),
    fanUpdate: (payload) => api.put("/fan/me", payload).then((r) => r.data),
    fanFollowing: (params) => api.get("/fan/following", { params }).then((r) => r.data),
    fanFollow: (artistId, source, sourceEventId) =>
        api.post(`/fan/following/${artistId}`, { source, source_event_id: sourceEventId }).then((r) => r.data),
    fanUnfollow: (artistId) => api.delete(`/fan/following/${artistId}`).then((r) => r.data),
    fanEvents: () => api.get("/fan/events").then((r) => r.data),
    fanRegister: (eventId) => api.post(`/fan/events/${eventId}/register`).then((r) => r.data),
    fanSaveEvent: (eventId) => api.post(`/fan/events/${eventId}/save`).then((r) => r.data),
    fanUnsaveEvent: (eventId) => api.delete(`/fan/events/${eventId}/save`).then((r) => r.data),
    fanOverview: () => api.get("/fan/overview").then((r) => r.data),
    fanDiscover: () => api.get("/fan/discover").then((r) => r.data),

    // QR / Pass / Scan
    getPass: (regId) => api.get(`/registrations/${regId}/pass`).then((r) => r.data),
    scanQR: (eventId, token) =>
        api.post(`/events/${eventId}/scan`, { token }).then((r) => r.data),
    updatePaymentStatus: (regId, paymentStatus) =>
        api.put(`/registrations/${regId}/payment_status`, { payment_status: paymentStatus }).then((r) => r.data),

    // Event Performance Intelligence
    eventPerformance: (eventId) =>
        api.get(`/events/${eventId}/performance`).then((r) => r.data),
    workspaceEventPerformance: (artistId) =>
        api.get(`/workspace/${artistId}/event_performance`).then((r) => r.data),

    // Follower Timeline
    artistFollowerTimeline: (artistId, days) =>
        api.get(`/artists/${artistId}/follower_timeline`, { params: days ? { days } : {} }).then((r) => r.data),

    // Follower Source Breakdown
    artistFollowerSources: (artistId, window) =>
        api.get(`/artists/${artistId}/follower_sources`, { params: window ? { window } : {} }).then((r) => r.data),

    // ---------- AUTH ----------
    authRegister: (payload) => api.post("/auth/register", payload).then((r) => r.data),
    authLogin: (payload) => api.post("/auth/login", payload).then((r) => r.data),
    authLogout: () => api.post("/auth/logout").then((r) => r.data),
    authMe: () => api.get("/auth/me").then((r) => r.data),
    authRefresh: () => api.post("/auth/refresh").then((r) => r.data),
    authForgot: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
    authReset: (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data),
    authGoogleConfig: () => api.get("/auth/google/config").then((r) => r.data),
    authGoogleCallback: (payload) => api.post("/auth/google/callback", payload).then((r) => r.data),
    authMyArtists: () => api.get("/auth/artists").then((r) => r.data),
    authMyVenues: () => api.get("/auth/venues").then((r) => r.data),
    authMyPromoters: () => api.get("/auth/promoters").then((r) => r.data),
    authMyLabels: () => api.get("/auth/labels").then((r) => r.data),
    orgAccessRequests: (kind, orgId) => api.get(`/${kind}-workspace/${orgId}/access-requests`).then((r) => r.data),
    orgApproveAccess: (kind, orgId, reqId, role) => api.post(`/${kind}-workspace/${orgId}/access-requests/${reqId}/approve`, { role }).then((r) => r.data),
    orgRejectAccess: (kind, orgId, reqId, reason) => api.post(`/${kind}-workspace/${orgId}/access-requests/${reqId}/reject`, { reason: reason || "" }).then((r) => r.data),
    promoterCreateEvent: (orgId, payload) => api.post(`/promoter-workspace/${orgId}/events`, payload).then((r) => r.data),
    promoterUpdateEvent: (orgId, eventId, payload) => api.put(`/promoter-workspace/${orgId}/events/${eventId}`, payload).then((r) => r.data),
    venueSearch: (q) => api.get("/venues", { params: { q } }).then((r) => r.data),
    artistSearch: (q) => api.get("/artists", { params: { q } }).then((r) => r.data),
    authActivateFanPassport: () => api.post("/auth/account/activate-fan-passport").then((r) => r.data),

    // Venue workspace
    venueOverview: (vid) => api.get(`/venue/${vid}/overview`).then((r) => r.data),
    venueEvents: (vid, status) => api.get(`/venue/${vid}/events`, { params: status ? { status } : {} }).then((r) => r.data),
    venueCreateEvent: (vid, payload) => api.post(`/venue/${vid}/events`, payload).then((r) => r.data),
    venueUpdateEvent: (vid, eid, payload) => api.put(`/venue/${vid}/events/${eid}`, payload).then((r) => r.data),
    venueDeleteEvent: (vid, eid) => api.delete(`/venue/${vid}/events/${eid}`).then((r) => r.data),
    venueAudience: (vid) => api.get(`/venue/${vid}/audience`).then((r) => r.data),
    venueUpdateProfile: (vid, payload) => api.put(`/venue/${vid}`, payload).then((r) => r.data),
    venueTeam: (vid) => api.get(`/venue/${vid}/team`).then((r) => r.data),
    venueRemoveMember: (vid, uid) => api.delete(`/venue/${vid}/members/${uid}`).then((r) => r.data),
    venueListInvitations: (vid) => api.get(`/venue/${vid}/invitations`).then((r) => r.data),
    venueCreateInvite: (vid, payload) => api.post(`/venue/${vid}/invitations`, payload).then((r) => r.data),
    venueRevokeInvite: (vid, iid) => api.delete(`/venue/${vid}/invitations/${iid}`).then((r) => r.data),
    venueShareLink: (vid, payload) => api.post(`/venue/${vid}/invitations/share-link`, payload).then((r) => r.data),

    // Venue event templates
    venueTemplates: (vid, includeArchived) => api.get(`/venue/${vid}/templates`, { params: includeArchived ? { include_archived: true } : {} }).then((r) => r.data),
    venueTemplate: (vid, tid) => api.get(`/venue/${vid}/templates/${tid}`).then((r) => r.data),
    venueCreateTemplate: (vid, payload) => api.post(`/venue/${vid}/templates`, payload).then((r) => r.data),
    venueUpdateTemplate: (vid, tid, payload) => api.put(`/venue/${vid}/templates/${tid}`, payload).then((r) => r.data),
    venueDuplicateTemplate: (vid, tid) => api.post(`/venue/${vid}/templates/${tid}/duplicate`).then((r) => r.data),
    venueArchiveTemplate: (vid, tid, archived) => api.post(`/venue/${vid}/templates/${tid}/archive`, { archived }).then((r) => r.data),
    venueDeleteTemplate: (vid, tid) => api.delete(`/venue/${vid}/templates/${tid}`).then((r) => r.data),
    venueSaveEventAsTemplate: (vid, eid) => api.post(`/venue/${vid}/events/${eid}/save-as-template`).then((r) => r.data),
    // Public venues directory
    listVenues: (filters) => api.get("/venues", { params: filters || {} }).then((r) => r.data),
    venuePublic: (vid) => api.get(`/venues/${vid}/public`).then((r) => r.data),

    // Venue follows (fan-facing)
    fanFollowVenue: (venueId, source, sourceEventId) =>
        api.post(`/fan/venue-follows/${venueId}`, { source, source_event_id: sourceEventId }).then((r) => r.data),
    fanUnfollowVenue: (venueId) =>
        api.delete(`/fan/venue-follows/${venueId}`).then((r) => r.data),
    fanFollowedVenues: () => api.get("/fan/venue-follows").then((r) => r.data),
    fanVenueUpcoming: () => api.get("/fan/venue-follows/upcoming").then((r) => r.data),
    fanRecommendedVenues: (limit) => api.get("/fan/recommended-venues", { params: limit ? { limit } : {} }).then((r) => r.data),

    // Venue follower analytics (venue manager / admin)
    venueFollowerSummary: (vid) => api.get(`/venues/${vid}/follower_summary`).then((r) => r.data),
    venueFollowerTimeline: (vid, days) => api.get(`/venues/${vid}/follower_timeline`, { params: days ? { days } : {} }).then((r) => r.data),
    venueFollowerSources: (vid, window) => api.get(`/venues/${vid}/follower_sources`, { params: window ? { window } : {} }).then((r) => r.data),
    venueEventFollowerGrowth: (vid, eid) => api.get(`/venues/${vid}/events/${eid}/follower_growth`).then((r) => r.data),

    // Weekly follower digest
    venueDigestPref: (vid) => api.get(`/venue/${vid}/settings/digest`).then((r) => r.data),
    venueUpdateDigestPref: (vid, enabled) => api.put(`/venue/${vid}/settings/digest`, { enabled }).then((r) => r.data),
    adminTestDigest: (payload) => api.post("/admin/venue-digest/test", payload).then((r) => r.data),
    adminDigestHistory: (limit) => api.get("/admin/venue-digest/history", { params: limit ? { limit } : {} }).then((r) => r.data),
    adminDigestSubscribe: (payload) => api.post("/admin/venue-digest/subscribe", payload).then((r) => r.data),

    // Artist pending events (venue-originated requests awaiting approval)
    artistPendingEvents: (aid) => api.get(`/artists/${aid}/pending-events`).then((r) => r.data),
    artistAcceptPendingEvent: (aid, eid) => api.post(`/artists/${aid}/pending-events/${eid}/accept`).then((r) => r.data),
    artistDeclinePendingEvent: (aid, eid) => api.post(`/artists/${aid}/pending-events/${eid}/decline`).then((r) => r.data),

    // Admin venue management
    adminAssignVenueManager: (vid, email) => api.post(`/admin/venues/${vid}/managers`, { user_email: email }).then((r) => r.data),
    adminListVenueMembers: (vid) => api.get(`/admin/venues/${vid}/members`).then((r) => r.data),
    adminRemoveVenueMember: (vid, uid) => api.delete(`/admin/venues/${vid}/members/${uid}`).then((r) => r.data),
    adminUpdateVenue: (vid, payload) => api.put(`/admin/venues/${vid}`, payload).then((r) => r.data),
    authAccountUpdate: (payload) => api.put("/auth/account", payload).then((r) => r.data),
    authCreateInvite: (artistId, payload) =>
        api.post(`/auth/artists/${artistId}/invitations`, payload).then((r) => r.data),
    authListInvites: (artistId) =>
        api.get(`/auth/artists/${artistId}/invitations`).then((r) => r.data),
    authResendInvite: (artistId, invitationId) =>
        api.post(`/auth/artists/${artistId}/invitations/${invitationId}/resend`).then((r) => r.data),
    authRevokeInvite: (artistId, invitationId) =>
        api.delete(`/auth/artists/${artistId}/invitations/${invitationId}`).then((r) => r.data),
    authAcceptInvite: (token) =>
        api.post("/auth/invitations/accept", { token }).then((r) => r.data),
    authInvitePreview: (token) =>
        api.get("/auth/invitations/preview", { params: { token } }).then((r) => r.data),
    authCreateShareLink: (artistId, payload) =>
        api.post(`/auth/artists/${artistId}/invitations/share-link`, payload).then((r) => r.data),
    authShareLinkSummary: (artistId) =>
        api.get(`/auth/artists/${artistId}/invitations/analytics/summary`).then((r) => r.data),
    venueShareLinkSummary: (vid) =>
        api.get(`/venue/${vid}/invitations/analytics/summary`).then((r) => r.data),

    onboardingState: (intent = "artist") =>
        api.get("/onboarding/state", { params: { intent } }).then((r) => r.data),
    upsertOnboardingState: (body) =>
        api.post("/onboarding/state", body).then((r) => r.data),
    submitArtistVerification: (artistId) =>
        api.post(`/workspace/${artistId}/verification/submit`).then((r) => r.data),
    getArtistVerification: (artistId) =>
        api.get(`/workspace/${artistId}/verification`).then((r) => r.data),
    adminListVerification: (status_filter) =>
        api.get("/admin/verification/artists", { params: status_filter ? { status_filter } : {} }).then((r) => r.data),
    adminVerifyArtist: (aid, note) =>
        api.post(`/admin/verification/artists/${aid}/verify`, { note: note || null }).then((r) => r.data),
    adminRequestArtistInfo: (aid, message) =>
        api.post(`/admin/verification/artists/${aid}/request-info`, { message }).then((r) => r.data),
    adminRejectArtist: (aid, reason) =>
        api.post(`/admin/verification/artists/${aid}/reject`, { reason }).then((r) => r.data),

    // Artist Onboarding & Catalogs
    listGenres: () => api.get("/genres").then((r) => r.data),
    addGenre: (value) => api.post("/genres", { value }).then((r) => r.data),
    listLanguages: () => api.get("/languages").then((r) => r.data),
    addLanguage: (value) => api.post("/languages", { value }).then((r) => r.data),
    artistSearch: (q) => api.get("/onboarding/artists/search", { params: { q } }).then((r) => r.data),
    onboardArtist: (payload) => api.post("/onboarding/artist", payload).then((r) => r.data),
    createArtistAccessRequest: (artistId, payload) =>
        api.post(`/artists/${artistId}/access-requests`, payload).then((r) => r.data),
    workspaceAccessRequests: (artistId) =>
        api.get(`/workspace/${artistId}/access-requests`).then((r) => r.data),
    approveAccessRequest: (artistId, reqId, payload) =>
        api.post(`/workspace/${artistId}/access-requests/${reqId}/approve`, payload || { granted_role: "team" }).then((r) => r.data),
    rejectAccessRequest: (artistId, reqId) =>
        api.post(`/workspace/${artistId}/access-requests/${reqId}/reject`).then((r) => r.data),
    onboardingStatus: (artistId) =>
        api.get(`/workspace/${artistId}/onboarding-status`).then((r) => r.data),
    publishArtist: (artistId) =>
        api.post(`/workspace/${artistId}/publish`).then((r) => r.data),
    unpublishArtist: (artistId) =>
        api.post(`/workspace/${artistId}/unpublish`).then((r) => r.data),
    adminAccessRequests: (statusFilter) =>
        api.get("/admin/access-requests", { params: statusFilter ? { status_filter: statusFilter } : {} }).then((r) => r.data),
    adminApproveAccessRequest: (reqId, payload) =>
        api.post(`/admin/access-requests/${reqId}/approve`, payload || { granted_role: "team" }).then((r) => r.data),
    adminRejectAccessRequest: (reqId) =>
        api.post(`/admin/access-requests/${reqId}/reject`).then((r) => r.data),

    // ---------- ADMIN ----------
    adminOverview: () => api.get("/admin/overview").then((r) => r.data),
    adminArtists: (params) => api.get("/admin/artists", { params }).then((r) => r.data),
    adminGetArtist: (id) => api.get(`/admin/artists/${id}`).then((r) => r.data),
    adminCreateArtist: (payload) => api.post("/admin/artists", payload).then((r) => r.data),
    adminUpdateArtist: (id, payload) => api.patch(`/admin/artists/${id}`, payload).then((r) => r.data),
    adminAssignOwner: (id, userId) => api.put(`/admin/artists/${id}/owner`, { user_id: userId }).then((r) => r.data),
    adminAddMember: (id, payload) => api.post(`/admin/artists/${id}/members`, payload).then((r) => r.data),
    adminRemoveMember: (id, userId) => api.delete(`/admin/artists/${id}/members/${userId}`).then((r) => r.data),
    adminUsers: (params) => api.get("/admin/users", { params }).then((r) => r.data),
    adminUpdateUser: (id, payload) => api.patch(`/admin/users/${id}`, payload).then((r) => r.data),
    adminUserDeletionPreview: (id) => api.get(`/admin/users/${id}/deletion-preview`).then((r) => r.data),
    adminDeleteUser: (id, payload) => api.delete(`/admin/users/${id}`, { data: payload }).then((r) => r.data),
    adminTransferOwnership: (payload) => api.post("/admin/workspaces/transfer-ownership", payload).then((r) => r.data),
    accountDeletionPreview: () => api.get("/account/deletion-preview").then((r) => r.data),
    accountTransferOwnership: (payload) => api.post("/account/workspaces/transfer-ownership", payload).then((r) => r.data),
    accountSelfDelete: (payload) => api.delete("/account/me", { data: payload }).then((r) => r.data),
    adminEvents: (params) => api.get("/admin/events", { params }).then((r) => r.data),
    adminUpdateEvent: (id, payload) => api.patch(`/admin/events/${id}`, payload).then((r) => r.data),
    adminVenues: (params) => api.get("/admin/venues", { params }).then((r) => r.data),
    adminCreateVenue: (payload) => api.post("/admin/venues", payload).then((r) => r.data),
    adminUpdateVenue: (id, payload) => api.put(`/admin/venues/${id}`, payload).then((r) => r.data),
    adminInvitations: () => api.get("/admin/invitations").then((r) => r.data),
    adminRevokeInvitation: (id) => api.delete(`/admin/invitations/${id}`).then((r) => r.data),
    adminResendInvitation: (id) => api.post(`/admin/invitations/${id}/resend`).then((r) => r.data),
    adminCreateShareLink: (artistId, payload) =>
        api.post(`/admin/artists/${artistId}/share-links`, payload).then((r) => r.data),
};

export function formatApiError(err) {
    const d = err?.response?.data?.detail;
    if (d == null) return err?.message || "Something went wrong. Please try again.";
    if (typeof d === "string") return d;
    if (Array.isArray(d))
        return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (d && typeof d.msg === "string") return d.msg;
    return String(d);
}
